import {
	Assessment as AssessmentIcon,
	Description as DescriptionIcon,
	Download as DownloadIcon,
	List as ListIcon,
	Refresh as RefreshIcon,
} from '@mui/icons-material';
import { FLAGS, NamespacedStoreState } from '@Types';
import React, { useEffect, useMemo } from 'react';
import {
	RightHeader,
	StyledHeaderText,
} from './SensorConfigurationsTable.styles';
import { SplitButton, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { getPhysicalSensorsCount, getVirtualSensorsCount } from '@Actions';
import { useConfigurationsPagination, useSensorsRedirects } from '@Hooks';
import { useDispatch, useSelector } from 'react-redux';

import FileUploadOutlineIcon from '@Components/icons/FileUploadOutlineIcon';
import ResampleSensorModal from '@Components/ResampleSensorModal';
import { SensorsReadingCountFileName } from '@Types/configurations.types';
import { SystemType } from '@Types/tiling.types';
import { getSensorsReadingsCount } from '@Actions/configurations';
import { getValue } from '@Selectors';
import { queueJob } from '@Reducers/tiling.reducer';
import { selectIsQueueBlocked } from '@Selectors/tiling.selectors';
import { selectPhysicalSensor } from '@Selectors/physicalSensor.selector';
import { selectVirtualSensor } from '@Selectors/virtualSensor.selector';
import { useGlobalization } from '@Translations';

interface SplitButtonOption {
	title: string;
	icon?: React.ReactNode;
	onClick: () => void;
	cy?: string;
	key?: number;
	disabled?: boolean;
}

interface NewSensorExtraContentProps {
	resampleModalOpen: boolean;
	setResampleModalOpen: (state: boolean) => void;
	jobsEnabled: boolean;
	setJobsEnabled: (state: boolean) => void;
}

const NewSensorExtraContent = (props: NewSensorExtraContentProps) => {
	const { t } = useGlobalization();
	const [
		redirectToCreateSensor,
		redirectToBulkSensorCreation,
		redirectToUploadSensorData,
	] = useSensorsRedirects();

	const [, , fetchConfigurationsRfi] = useConfigurationsPagination();

	const isResampleJobsEnabled = !!useIsFeatureEnabled(FLAGS.RESAMPLE_JOBS);
	const regenerateTilesEnabled = useIsFeatureEnabled(
		FLAGS.REGENERATE_TILES_SENSOR,
	);
	const isRegenerateTilesButtonDisabled = useSelector(
		(state: NamespacedStoreState) =>
			selectIsQueueBlocked(state, SystemType.Sensors),
	);

	//For virtual sensors count
	const dispatch = useDispatch();
	const { virtualCount } = useSelector(selectVirtualSensor);
	const { physicalCount } = useSelector(selectPhysicalSensor);

	const {
		configurations: { checkOrUncheckedRows, headerCheckboxChecked },
	} = useSelector(getValue);

	useEffect(() => {
		dispatch(getVirtualSensorsCount());
		dispatch(getPhysicalSensorsCount());
	}, []);

	const selectedSensorIds = useMemo(() => {
		if (headerCheckboxChecked) return ['*'];
		return checkOrUncheckedRows as never as string[]; // ! Important: This case comes from NEW_MUIX_TABLE
	}, [headerCheckboxChecked, checkOrUncheckedRows]);

	const handleOnClickNewSensorButton = (): void => {
		redirectToCreateSensor();
	};
	const handleOnClickBulkCreationButton = (): void => {
		redirectToBulkSensorCreation();
	};
	const handleOnClickUploadDataButton = (): void => {
		redirectToUploadSensorData();
	};
	const handleOnDownloadRFIOptionOptionButton = (): void => {
		fetchConfigurationsRfi(1000);
	};
	const handleOnClickBulkResampleLegacyButton = (): void => {
		props.setJobsEnabled(false);
		props.setResampleModalOpen(true);
	};

	const closeResampleModalHandler = (): void => {
		props.setResampleModalOpen(false);
	};

	const handleDownloadSensorDataButton = (): void => {
		dispatch(
			getSensorsReadingsCount({ fileName: SensorsReadingCountFileName }),
		);
	};

	const actionOptions: SplitButtonOption[] = [
		{
			cy: 'newSensorButton',
			title: t('New Sensor'),
			icon: <ListIcon style={{ color: '#768D95' }} />,
			onClick: handleOnClickNewSensorButton,
		},
		{
			title: t('Bulk Sensor Creation'),
			icon: (
				<ListIcon
					style={{ color: '#768D95' }}
					data-cy="bulkSensorOption"
				/>
			),
			onClick: handleOnClickBulkCreationButton,
		},
	];
	if (!isResampleJobsEnabled) {
		actionOptions.push({
			cy: 'bulkResampleOption',
			title: t('Bulk Resample'),
			icon: (
				<AssessmentIcon
					style={{ color: '#768D95' }}
					data-cy="bulkResampleOption"
				/>
			),
			onClick: handleOnClickBulkResampleLegacyButton,
			disabled: false,
		});
	}
	const nextOptions = [
		{
			cy: 'downloadRFIOption',
			title: t('Download RFI'),
			icon: (
				<DescriptionIcon
					style={{ color: '#768D95' }}
					data-cy="downloadRFIOption"
				/>
			),
			onClick: handleOnDownloadRFIOptionOptionButton,
		},
		{
			cy: 'uploadDataOption',
			title: t('Upload Data'),
			icon: <FileUploadOutlineIcon data-cy="UploadSensorDataOption" />,
			onClick: handleOnClickUploadDataButton,
		},
		{
			cy: 'downloadSensorsCountOption',
			title: t('Download Sensor Reading Count'),
			icon: (
				<DownloadIcon
					style={{ color: '#768D95' }}
					data-cy="downloadSensorsCount"
				/>
			),
			onClick: handleDownloadSensorDataButton,
		},
	];
	actionOptions.push(...nextOptions);

	if (regenerateTilesEnabled) {
		actionOptions.push({
			title: t('Refresh Sensors On Map'),
			icon: (
				<RefreshIcon
					style={{ color: '#768D95' }}
					data-cy="generateSensorTiles"
				/>
			),
			onClick: () => {
				dispatch(
					queueJob({
						systemType: SystemType.Sensors,
					}),
				);
			},
			disabled: isRegenerateTilesButtonDisabled,
		});
	}

	return (
		<RightHeader>
			<StyledHeaderText>
				{t('Physical Sensors Used: {{total}} / ', {
					total: physicalCount ?? 0,
				})}
				{t('Virtual Sensors Used: {{total}}', {
					total: virtualCount ?? 0,
				})}
			</StyledHeaderText>
			<SplitButton
				popupZIndex={2}
				oneClickMenu={true}
				includeSelectedInMenu={false}
				options={actionOptions || null}
			/>
			<ResampleSensorModal
				open={props.resampleModalOpen}
				sensorId="*"
				sensorIds={selectedSensorIds}
				onClose={closeResampleModalHandler}
				jobsEnabled={props.jobsEnabled}
			/>
		</RightHeader>
	);
};

export default NewSensorExtraContent;
